// TODO [DASH-8441]: Remove this normalizer once consolidated practice is GA —
// consumers should use ConsolidatedPractice fields directly (name, paymentId, etc.)
// instead of the legacy IPractice shape.
import { ConsolidatedPractice, Practice } from 'gql/generated-types/custom-graphql'

/**
 * Maps a consolidated practice object to the IPractice shape used throughout
 * the frontend. Consumers keep their existing field access patterns (e.g.
 * `practice.clinicName`, `practice.stripeAccountId`) without changes.
 *
 * @param cp - Consolidated practice from GraphQL or REST API.
 * @returns An IPractice-shaped object with mapped fields.
 */
export const normalizeConsolidatedPractice = (cp: ConsolidatedPractice): IPractice => ({
	id: cp.id,
	cuid: cp.paymentId ?? undefined,
	stripeAccountId: cp.paymentId ?? undefined,
	transferDataDestination: cp.paymentId ?? undefined,
	vetClinicId: cp.lendingId ?? undefined,
	clinicName: cp.name ?? undefined,
	clinicAddress: cp.address ?? undefined,
	clinicCity: cp.city ?? undefined,
	clinicState: cp.state ?? undefined,
	clinicZip: cp.zip ?? undefined,
	clinicCountry: (cp.country as 'US' | 'CA') ?? undefined,
	country: (cp.country as 'US' | 'CA') ?? undefined,
	clinicPhone: cp.phone ?? undefined,
	clinicGeneralEmail: cp.email ?? undefined,
	clinicEmail: cp.email ?? undefined,
	clinicWebsite: cp.website ?? undefined,
	clinicHospitalManagerEmail: cp.hospitalManagerEmail ?? undefined,
	consolidatorId: cp.consolidatorId ?? undefined,
	consolidator: cp.consolidator ?? undefined,
	consolidatorReferenceCodeforClinic: cp.consolidatorReferenceCode ?? undefined,
	consolidatorReferenceCodeforClinic2: cp.consolidatorReferenceCode2 ?? undefined,
	consolidatorReferenceCodeforClinic3: cp.consolidatorReferenceCode3 ?? undefined,
	terminalLocation: cp.terminalLocation ?? undefined,
	signerEmail: cp.signerEmail ?? undefined,
	signerName: cp.signerName ?? undefined,
	netsuiteId: cp.netsuiteId ?? undefined,
	skyworldId: cp.skyworldId ?? undefined,
	logoUrl: cp.logoUrl ?? undefined,
	regionalManagerId: cp.regionalManagerId ?? undefined,
	settings: cp.settings ?? undefined,
	clinicPIMsLocationID: (cp.pimsMetadata as Record<string, unknown>)?.pimsLocationId as
		| string
		| undefined,
	currentPims: (cp.pimsMetadata as Record<string, unknown>)?.currentPims as string | undefined,
	clinicTimezone: cp.timezone ?? undefined,
	surchargeEnabled: cp.surchargeEnabled ?? undefined,
	surchargeFeePct: cp.surchargeFeePct ?? undefined,
	transactionFees: cp.transactionFees ?? undefined,
	isActive: cp.isActive ?? undefined,
	createdAt: cp.createdAt ?? undefined,
	updatedAt: cp.updatedAt ?? undefined
})

/**
 * Normalizes an array of consolidated practices to IPractice[].
 */
export const normalizeConsolidatedPractices = (cps: ConsolidatedPractice[]): IPractice[] =>
	cps.map(normalizeConsolidatedPractice)

// TODO [DASH-8441]: Remove once consolidated practice is GA — delete legacy path.
/**
 * Maps a legacy GQL Practice to ConsolidatedPractice shape so downstream
 * components can use `paymentId` consistently.
 *
 * Inverse of {@link normalizeConsolidatedPractice}. Every renamed field is
 * mapped explicitly so nothing is silently dropped when schemas diverge.
 *
 * Accepts a partial Practice so callers holding a narrow grid selection (name,
 * netsuiteId, …) can map it too; unselected fields come back undefined.
 *
 * @param p - Practice from legacy GraphQL query, whole or partial.
 * @returns A ConsolidatedPractice-shaped object with mapped fields.
 */
export const normalizePracticeToConsolidated = (p: Partial<Practice>): ConsolidatedPractice =>
	({
		id: p.id,
		paymentId: p.stripeAccountId,
		lendingId: p.vetClinicId,
		name: p.name,
		address: p.address,
		city: p.city,
		state: p.state,
		zip: p.zip,
		country: p.country,
		email: p.email,
		website: p.website,
		timezone: p.timezone,
		hospitalManagerEmail: p.hospitalManagerEmail,
		consolidatorId: p.consolidatorId,
		consolidator: p.consolidator,
		consolidatorReferenceCode: p.consolidatorReferenceCode,
		consolidatorReferenceCode2: p.consolidatorReferenceCode2,
		consolidatorReferenceCode3: p.consolidatorReferenceCode3,
		terminalLocation: p.terminalLocation,
		netsuiteId: p.netsuiteId,
		skyworldId: p.skyworldId,
		logoUrl: p.logoUrl,
		regionalManagerId: p.regionalManagerId,
		settings: p.settings,
		pimsMetadata: p.currentPims ? { currentPims: p.currentPims } : undefined,
		isDeleted: p.deleted,
		isActive: !p.deleted,
		isPaymentEnabled: p.isPaymentEnabled,
		isLendingEnabled: p.isLendingEnabled,
		isRebateEnabled: p.isRebateEnabled,
		isCustomPayoutEnabled: p.isCustomPayoutEnabled,
		customPayoutTarget: p.customPayoutTarget,
		customPayoutSettings: p.customPayoutSettings,
		surchargeEnabled: p.surchargeEnabled,
		surchargeFeePct: p.surchargeFeePct,
		financingStatus: p.financingStatus,
		isUnderwritten: p.isUnderwritten,
		underwritingStatus: p.underwrittingStatus,
		lendingFlags: p.originationFlags,
		integrationId: p.integrationId,
		activeTerminalsCount: p.activeTerminalsCount,
		totalTerminalsCount: p.totalTerminalsCount,
		industryProgramMetadata: p.industryProgramMetadata,
		socialUrl: p.socialUrl,
		transactionFees: undefined, // not on Practice GQL type
		createdAt: p.createdAt,
		updatedAt: p.updatedAt
	}) as ConsolidatedPractice
